import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type {
  NotificationProviderStatus,
  SmsProviderConfiguration,
} from '@/api/notificationProviders/notificationProvidersApi.types';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/passwordInput';
import {
  useConfigureSmsProviderMutation,
  useRemoveSmsProviderMutation,
} from '@/hooks/useNotificationProviders';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { ProviderField } from '../ProviderField';
import { ProviderStatusBadge } from '../ProviderStatusBadge';
import { SmsProviderSummary } from './SmsProviderSummary';
import { SmsProviderTestPanel } from './SmsProviderTestPanel';
import {
  hasSmsProviderFormErrors,
  normalizeSmsProviderConfiguration,
  validateSmsProviderConfiguration,
  type SmsProviderFormErrors,
} from './smsProviderValidation';

const EMPTY_CONFIGURATION: SmsProviderConfiguration = {
  accountSid: '',
  apiKeySid: '',
  apiKeySecret: '',
  fromNumber: '',
  messagingServiceSid: '',
};

function describeSavedConfiguration(status: NotificationProviderStatus | undefined): string {
  if (status === undefined || status.isConfigured === false) {
    return 'unconfigured';
  }
  return [status.accountSidSuffix, status.messagingServiceSid, status.senderNumber].join('|');
}

interface SmsProviderCardProps {
  status: NotificationProviderStatus | undefined;
}

export function SmsProviderCard({ status }: SmsProviderCardProps) {
  const { t } = useTranslation('notifications');
  const configureProvider = useConfigureSmsProviderMutation();
  const removeProvider = useRemoveSmsProviderMutation();
  const [configuration, setConfiguration] = useState<SmsProviderConfiguration>(EMPTY_CONFIGURATION);
  const [errors, setErrors] = useState<SmsProviderFormErrors>({});
  const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false);
  const isConfigured = status !== undefined && status.isConfigured === true;

  function updateField(field: keyof SmsProviderConfiguration, value: string) {
    setConfiguration(function applyChange(previous) {
      return { ...previous, [field]: value };
    });
  }

  function translateError(field: keyof SmsProviderConfiguration): string | undefined {
    const errorKey = errors[field];
    return errorKey === undefined ? undefined : t('providers.sms.validation.' + errorKey);
  }

  function save() {
    const normalized = normalizeSmsProviderConfiguration(configuration);
    const validationErrors = validateSmsProviderConfiguration(normalized);
    setErrors(validationErrors);
    if (hasSmsProviderFormErrors(validationErrors)) {
      return;
    }
    configureProvider.mutate(normalized, {
      onSuccess: function reportSaved() {
        setConfiguration(EMPTY_CONFIGURATION);
        showSuccessNotification(t('providers.saved'));
      },
      onError: function reportError(error) {
        const message = resolveApiErrorMessage(error);
        showErrorNotification(message.length > 0 ? message : t('providers.saveError'));
      },
    });
  }

  function remove() {
    removeProvider.mutate(undefined, {
      onSuccess: function reportRemoved() {
        setIsConfirmingRemoval(false);
        showSuccessNotification(t('providers.sms.removed'));
      },
      onError: function reportRemovalError() {
        setIsConfirmingRemoval(false);
        showErrorNotification(t('providers.removeError'));
      },
    });
  }

  return (
    <div className="border border-zooborder rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="text-[13px] font-bold text-zooindigo">{t('providers.sms.title')}</h3>
        <ProviderStatusBadge status={status} />
      </div>
      <p className="text-xs text-zoomuted mb-1">{t('providers.sms.description')}</p>
      <p className="text-xs text-zoomuted mb-4">{t('providers.sms.help')}</p>

      {isConfigured && status !== undefined ? <SmsProviderSummary status={status} /> : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ProviderField
          label={t('providers.sms.accountSid')}
          htmlFor="sms-provider-account-sid"
          hint={t('providers.sms.accountSidHint')}
          error={translateError('accountSid')}
          isRequired
        >
          <Input
            id="sms-provider-account-sid"
            value={configuration.accountSid}
            hasError={errors.accountSid !== undefined}
            autoComplete="off"
            onChange={(event) => updateField('accountSid', event.target.value)}
          />
        </ProviderField>
        <ProviderField
          label={t('providers.sms.apiKeySid')}
          htmlFor="sms-provider-api-key-sid"
          hint={t('providers.sms.apiKeySidHint')}
          error={translateError('apiKeySid')}
          isRequired
        >
          <Input
            id="sms-provider-api-key-sid"
            value={configuration.apiKeySid}
            hasError={errors.apiKeySid !== undefined}
            autoComplete="off"
            onChange={(event) => updateField('apiKeySid', event.target.value)}
          />
        </ProviderField>
        <ProviderField
          label={t('providers.sms.apiKeySecret')}
          htmlFor="sms-provider-api-key-secret"
          hint={t('providers.sms.apiKeySecretHint')}
          error={translateError('apiKeySecret')}
          isRequired
        >
          <PasswordInput
            id="sms-provider-api-key-secret"
            value={configuration.apiKeySecret}
            hasError={errors.apiKeySecret !== undefined}
            autoComplete="off"
            onChange={(event) => updateField('apiKeySecret', event.target.value)}
          />
        </ProviderField>
        <ProviderField
          label={t('providers.sms.messagingServiceSid')}
          htmlFor="sms-provider-messaging-service-sid"
          hint={t('providers.sms.messagingServiceSidHint')}
          error={translateError('messagingServiceSid')}
        >
          <Input
            id="sms-provider-messaging-service-sid"
            value={configuration.messagingServiceSid}
            hasError={errors.messagingServiceSid !== undefined}
            autoComplete="off"
            onChange={(event) => updateField('messagingServiceSid', event.target.value)}
          />
        </ProviderField>
        <ProviderField
          label={t('providers.sms.fromNumber')}
          htmlFor="sms-provider-from-number"
          hint={t('providers.sms.fromNumberHint')}
          error={translateError('fromNumber')}
        >
          <Input
            id="sms-provider-from-number"
            value={configuration.fromNumber}
            hasError={errors.fromNumber !== undefined}
            autoComplete="off"
            onChange={(event) => updateField('fromNumber', event.target.value)}
          />
        </ProviderField>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <Button onClick={save} isLoading={configureProvider.isPending}>
          {t('providers.save')}
        </Button>
        {isConfigured ? (
          <Button variant="outline" onClick={() => setIsConfirmingRemoval(true)}>
            {t('providers.sms.remove')}
          </Button>
        ) : null}
      </div>

      <SmsProviderTestPanel key={describeSavedConfiguration(status)} isConfigured={isConfigured} />

      {isConfirmingRemoval ? (
        <ConfirmDialog
          title={t('providers.sms.removeConfirmTitle')}
          description={t('providers.sms.removeConfirmDescription')}
          confirmLabel={t('providers.sms.remove')}
          tone="danger"
          isConfirming={removeProvider.isPending}
          onConfirm={remove}
          onCancel={() => setIsConfirmingRemoval(false)}
        />
      ) : null}
    </div>
  );
}
